import { ApiClient } from '@/lib/api-client';
import { 
  Recipe,
  RecipeSearchParams,
  RecipeSearchResult
} from '@/types/api';

export class RecipesService {
  /**
   * Search recipes with filters
   */
  static async searchRecipes(params: RecipeSearchParams = {}): Promise<RecipeSearchResult> {
    const searchParams = new URLSearchParams();
    
    // Add parameters if they exist
    if (params.query) searchParams.append('query', params.query);
    if (params.category) searchParams.append('category', params.category);
    if (params.area) searchParams.append('area', params.area);
    if (params.ingredient) searchParams.append('ingredient', params.ingredient);
    if (params.maxTime) searchParams.append('maxTime', params.maxTime.toString());
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    
    // Always send lang=es for Spanish translation
    searchParams.append('lang', 'es');

    const url = `/recipes${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await ApiClient.get<RecipeSearchResult>(url);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to search recipes');
  }

  /**
   * Get random recipes
   */
  static async getRandomRecipes(count: number = 10): Promise<Recipe[]> {
    const response = await ApiClient.get<Recipe[]>(`/recipes/random?count=${count}`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get random recipes');
  }

  /**
   * Get recipe by ID
   */
  static async getRecipeById(id: string): Promise<Recipe> {
    const response = await ApiClient.get<Recipe>(`/recipes/${id}`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get recipe');
  }

  /**
   * Get all categories
   */
  static async getCategories(): Promise<string[]> {
    const response = await ApiClient.get<string[]>('/recipes/categories');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get categories');
  }

  /**
   * Get all areas/cuisines
   */
  static async getAreas(): Promise<string[]> {
    const response = await ApiClient.get<string[]>('/recipes/areas');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get areas');
  }

  /**
   * Get all ingredients
   */
  static async getIngredients(): Promise<string[]> {
    const response = await ApiClient.get<string[]>('/recipes/ingredients');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get ingredients');
  }

  /**
   * Clear recipe cache (admin)
   */
  static async clearCache(): Promise<void> {
    const response = await ApiClient.post('/recipes/admin/cache/clear');
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to clear cache');
    }
  }

  /**
   * Get cache stats (admin)
   */
  static async getCacheStats(): Promise<{ redis: boolean; memoryKeys: number }> {
    const response = await ApiClient.get<{ redis: boolean; memoryKeys: number }>('/recipes/admin/cache/stats');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get cache stats');
  }
}
